import { useState } from "react";
import { adminApi } from "@/api/admin";
import type { LookupItem, SimulaResult } from "@/types/admin";
import { Autocomplete } from "./Autocomplete";

// Le tre cifre di uno scenario. I valori arrivano già calcolati dal backend: qui non
// si fa aritmetica sul denaro, così la formula vive in un solo posto.
function Scenario({ ricavo, quota, margine }: {
  ricavo?: number | null;
  quota?: number | null;
  margine?: number | null;
}) {
  const righe: [string, number | null | undefined][] = [
    ["Ricavo", ricavo],
    ["Quota agenzia", quota],
    ["Margine", margine],
  ];
  return (
    <dl className="space-y-1.5">
      {righe.map(([label, v], i) => (
        <div key={label} className="flex items-baseline justify-between gap-3">
          <dt className="text-sm text-muted-foreground">{label}</dt>
          <dd
            className={`tabular-nums ${
              i === 2 ? "text-base font-semibold text-foreground" : "text-sm font-medium text-foreground"
            }`}
          >
            {v != null ? `${v}€` : "—"}
          </dd>
        </div>
      ))}
    </dl>
  );
}

export function Simulatore() {
  const [agenzia, setAgenzia] = useState<LookupItem | null>(null);
  const [offerta, setOfferta] = useState<LookupItem | null>(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<SimulaResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function handleSimula(e: React.FormEvent) {
    e.preventDefault();
    if (!agenzia) return;
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const res = await adminApi.simula(agenzia.id, offerta?.id);
      setResult(res);
    } catch {
      setError("Errore durante la simulazione. Riprovare.");
    } finally {
      setLoading(false);
    }
  }

  const noTariff = result !== null && (result.tier === null || result.valore === null);
  const hasSdd = result !== null && (result.tier_sdd != null || result.valore_sdd != null);
  // Ricavo e margine esistono solo se l'offerta è stata scelta e ha il ricavo configurato.
  const senzaRicavo = result !== null && result.ricavo == null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-lg font-semibold">Simulatore Commissioni</h2>
        <p className="text-sm text-muted-foreground">
          Calcola la tariffa applicabile a un'agenzia (e opzionalmente un'offerta specifica).
        </p>
      </div>

      {/* Form */}
      <form onSubmit={handleSimula} className="bg-card border border-border rounded-xl p-5 space-y-4 max-w-md">
        <Autocomplete
          id="simula-agenzia"
          label="Agenzia"
          required
          value={agenzia}
          onChange={setAgenzia}
          fetchItems={(q) => adminApi.lookupAgenzie(q)}
          placeholder="Cerca agenzia..."
        />

        <Autocomplete
          id="simula-offerta"
          label="Offerta (opzionale)"
          value={offerta}
          onChange={setOfferta}
          fetchItems={(q) => adminApi.lookupOfferte(q)}
          placeholder="Cerca offerta..."
        />

        <button
          type="submit"
          disabled={!agenzia || loading}
          className="w-full px-4 py-2 text-sm bg-primary text-primary-foreground rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 font-medium"
        >
          {loading ? "Calcolo in corso..." : "Simula"}
        </button>
      </form>

      {/* Error */}
      {error && (
        <p className="text-sm text-destructive">{error}</p>
      )}

      {/* Result cards — due scenari affiancati: senza RID / con RID */}
      {result !== null && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-3xl">
          {/* Senza RID */}
          <div
            className={`rounded-xl border p-6 space-y-3 ${
              noTariff ? "border-amber-300 bg-amber-50" : "border-border bg-card"
            }`}
          >
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Senza RID
            </h3>
            <Scenario
              ricavo={result.ricavo}
              quota={result.valore}
              margine={result.margine}
            />

            <p className="text-sm text-muted-foreground">
              Vince il{" "}
              {result.tier !== null ? `gradino ${result.tier}` : "—"}. {result.spiegazione}
            </p>

            {noTariff && (
              <p className="text-sm font-medium text-amber-700">
                Nessuna tariffa applicabile — sistemare il listino.
              </p>
            )}
          </div>

          {/* Con RID (SDD) */}
          <div className="rounded-xl border border-border bg-card p-6 space-y-3">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Con RID (SDD)
            </h3>
            <Scenario
              ricavo={result.ricavo_sdd_totale}
              quota={result.quota_sdd_totale}
              margine={result.margine_sdd}
            />

            {hasSdd ? (
              <p className="text-sm text-muted-foreground">
                Extra SDD (gradino{" "}
                {result.tier_sdd !== null && result.tier_sdd !== undefined
                  ? result.tier_sdd
                  : "—"}
                ): +{result.valore_sdd ?? 0}€ quota
                {result.valore_ricavo_sdd != null &&
                  ` / +${result.valore_ricavo_sdd}€ ricavo`}
                . Si somma alla base, non la sostituisce.
              </p>
            ) : (
              <p className="text-sm text-muted-foreground">
                Nessuna riga SDD nel listino per questa combinazione — con RID i
                numeri restano identici a "senza RID".
              </p>
            )}
          </div>

          {senzaRicavo && (
            <p className="sm:col-span-2 text-sm text-muted-foreground">
              Ricavo e margine non calcolabili: scegli un'offerta con il ricavo AEF
              configurato.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
